import React, { useState, useEffect } from 'react';
import { Bell, X, Mail, Smartphone, Sun, Settings2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { REMINDER_OPTIONS } from './calendarConfig';

// ============================================================================
// REMINDER SETTINGS COMPONENT
// ============================================================================
export const ReminderSettings = ({ reminders, onChange }) => {
  const addReminder = (minutes) => {
    if (!reminders.includes(minutes)) {
      onChange([...reminders, minutes].sort((a, b) => a - b));
    }
  };

  const removeReminder = (minutes) => {
    onChange(reminders.filter(r => r !== minutes));
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Bell className="h-4 w-4" />
        Reminders
      </Label>
      
      {/* Current reminders */}
      {reminders.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {reminders.map(mins => {
            const option = REMINDER_OPTIONS.find(o => o.value === mins);
            return (
              <Badge key={mins} variant="secondary" className="gap-1 text-xs">
                <Bell className="h-3 w-3" />
                {option?.label || `${mins} mins`}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => removeReminder(mins)}
                />
              </Badge>
            );
          })}
        </div>
      )}
      
      {/* Add reminder dropdown */}
      <Select onValueChange={(v) => addReminder(parseInt(v))}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Add reminder..." />
        </SelectTrigger>
        <SelectContent>
          {REMINDER_OPTIONS.filter(o => !reminders.includes(o.value)).map(option => (
            <SelectItem key={option.value} value={option.value.toString()}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// ============================================================================
// NOTIFICATION PREFERENCES DIALOG
// ============================================================================
export const NotificationPreferencesDialog = ({ open, onOpenChange, preferences, onSave }) => {
  const [localPrefs, setLocalPrefs] = useState(preferences);

  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  const handleSave = () => {
    onSave(localPrefs);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Configure how you want to receive calendar notifications
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* In-app notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Bell className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <Label className="font-medium">In-app notifications</Label>
                <p className="text-xs text-muted-foreground">Show popup alerts in the app</p>
              </div>
            </div>
            <Switch
              checked={localPrefs.inApp}
              onCheckedChange={(v) => setLocalPrefs({ ...localPrefs, inApp: v })}
            />
          </div>

          {/* Email notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Mail className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <Label className="font-medium">Email notifications</Label>
                <p className="text-xs text-muted-foreground">Send reminders to your email</p>
              </div>
            </div>
            <Switch
              checked={localPrefs.email}
              onCheckedChange={(v) => setLocalPrefs({ ...localPrefs, email: v })}
            />
          </div>

          {/* SMS notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Smartphone className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <Label className="font-medium">SMS notifications</Label>
                <p className="text-xs text-muted-foreground">Send text message reminders</p>
              </div>
            </div>
            <Switch
              checked={localPrefs.sms}
              onCheckedChange={(v) => setLocalPrefs({ ...localPrefs, sms: v })}
            />
          </div>

          <Separator />

          {/* Daily agenda */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Sun className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <Label className="font-medium">Daily agenda email</Label>
                <p className="text-xs text-muted-foreground">Get a summary every morning</p>
              </div>
            </div>
            <Switch
              checked={localPrefs.dailyAgenda}
              onCheckedChange={(v) => setLocalPrefs({ ...localPrefs, dailyAgenda: v })}
            />
          </div>

          {/* Default reminder */}
          <div>
            <Label className="mb-2 block">Default reminder time</Label>
            <Select 
              value={localPrefs.defaultReminder?.toString()} 
              onValueChange={(v) => setLocalPrefs({ ...localPrefs, defaultReminder: parseInt(v) })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select default reminder" />
              </SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave}>Save Preferences</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
